import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dismissalOrders, employees } from '@/api/hr';
import { vacationCompensation } from '@/api/vacation';
import { esvTipDoc, trimNum } from '@/shared/taxTips';
import InfoQ from '@/shared/components/InfoQ';
import { orgs } from '@/api/catalogs';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useFormValidation } from '@/hooks/useFormValidation';
import DocumentToolbar from '@/shared/components/DocumentToolbar';

interface Form {
  organization_id: string;
  document_number: string;
  document_date: string;
  employee_id: string;
  dismiss_date: string;
  reason_article: string;
  reason: string;
  compensation_amount: string;
  comment: string;
}

const today = new Date();
const EMPTY: Form = {
  organization_id: '', document_number: '',
  document_date: today.toISOString().slice(0, 16),
  employee_id: '',
  dismiss_date: today.toISOString().slice(0, 10),
  reason_article: 'ст. 38 КЗпП України — за власним бажанням',
  reason: '',
  compensation_amount: '0',
  comment: '',
};

const KZOT_ARTICLES = [
  'ст. 38 КЗпП України — за власним бажанням',
  'ст. 39 КЗпП України — розірвання строкового договору',
  'ст. 40 КЗпП України — з ініціативи власника',
  'ст. 41 КЗпП України — додаткові підстави звільнення',
  'ст. 36 п. 1 КЗпП — угода сторін',
  'ст. 36 п. 5 КЗпП — переведення працівника',
];

export default function DismissalOrderEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const [search] = useSearchParams();
  const isNew = !id || id === 'new';
  const empFromUrl = search.get('employee');

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX β: фільтр org dropdown по payroll.manage permission
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg('payroll.manage');
  const docQuery = useQuery({
    queryKey: ['dismissal-order', id],
    queryFn: () => dismissalOrders.get(id!),
    enabled: !isNew,
  });
  const [form, setForm] = useState<Form>(EMPTY);
  // Незбережені правки наказа → деактивують «Розрахувати компенсацію» (preview застарів).
  const [dirty, setDirty] = useState(false);
  const numberPreview = useNumberPreview('doc.dismissal_orders', form.organization_id, isNew);
  const v = useFormValidation();

  // Зміна будь-якого поля наказа → dirty + скидання компенсаційного preview.
  function setF(patch: Partial<Form>) {
    setForm((f) => ({ ...f, ...patch }));
    setDirty(true);
    setCompPreview(null);
  }

  function validate(): boolean {
    return v.check([
      ['organization_id', form.organization_id, 'Оберіть організацію'],
      ['document_date', form.document_date, 'Вкажіть дату наказу'],
      ['employee_id', form.employee_id, 'Оберіть співробітника'],
      ['dismiss_date', form.dismiss_date, 'Вкажіть дату звільнення'],
    ]);
  }
  function onSave() {
    if (!validate()) return;
    isNew ? create.mutate() : update.mutate();
  }
  const [error, setError] = useState<string | null>(null);
  // D: розрахунок компенсації відпустки прямо у наказі (preview + збереження окремої відомості).
  const [compPreview, setCompPreview] = useState<Record<string, string> | null>(null);
  // Id вже створеного нарахування компенсації цього наказа (щоб ОНОВЛЮВАТИ, не дублювати).
  const [existingCompId, setExistingCompId] = useState<string | null>(null);
  // Редаговані дні + середня (override) — бухгалтер може перебити перед збереженням.
  const [compDays, setCompDays] = useState('');
  const [compAvg, setCompAvg] = useState('');
  // Розрахунок компенсації ДО збереження — за employee_id + dismiss_date (без id наказа).
  const compPreviewMut = useMutation({
    mutationFn: () => vacationCompensation.previewByParams(
      form.organization_id, form.employee_id, form.dismiss_date,
      compDays || null, compAvg || null),
    onSuccess: (r) => {
      setCompPreview(r); setError(null);
      // Підставити пораховані дні/середню у поля (щоб бухгалтер бачив і міг перебити).
      if (!compDays) setCompDays(String(r.compensation_days ?? ''));
      if (!compAvg) setCompAvg(String(r.average_daily_wage ?? ''));
    },
    onError: (e) => setError(extractError(e)),
  });
  // Зберегти нарахування з РУЧНИМИ дні/середня (compDays/compAvg з preview).
  // Якщо нарахування вже є → update. Iнакше створити (fromDismissal) і ОДРАЗУ
  // застосувати ручні значення (інакше збережеться авто-середня).
  const compSaveMut = useMutation({
    mutationFn: async () => {
      let compId = existingCompId;
      let d = compDays;
      let a = compAvg;
      if (!compId) {
        const created = await vacationCompensation.fromDismissal(form.organization_id, id!);
        compId = created.id;
        setExistingCompId(created.id);
        // Дефолти з авто-розрахунку, якщо бухгалтер не перебивав поля.
        if (!d) d = String(created.compensation_days);
        if (!a) a = String(created.average_daily_wage);
      }
      // Записати ручні (або авто) дні+середню → точні суми у нарахуванні.
      return vacationCompensation.update(compId, d || '0', a || '0');
    },
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ['vacation-compensations'] });
      qc.invalidateQueries({ queryKey: ['accruals-registry'] });
      navigate('/payrolls');
    },
    onError: (e) => setError(extractError(e)),
  });
  // Сторінка створення: зберегти чернетку і одразу провести (create → post → картка).
  async function onSaveAndPost() {
    if (!validate()) return;
    setError(null);
    try {
      const doc = await create.mutateAsync();
      await dismissalOrders.post(doc.id);
      qc.invalidateQueries({ queryKey: ['dismissal-orders'] });
      qc.invalidateQueries({ queryKey: ['employees-full'] });
      navigate(`/dismissal-orders/${doc.id}`);
    } catch (e) {
      setError(extractError(e));
    }
  }
  // Зберегти ручний розрахунок компенсації (дні/середня) ПЕРЕД проведенням —
  // інакше post авто-створить компенсацію з балансу і втратить ручні правки.
  // post idempotent: якщо нарахування вже є, авто-створення поверне його.
  async function ensureCompSaved() {
    if (!compPreview) return;  // не рахували — авто-створення при post (ок)
    if (existingCompId) {
      await vacationCompensation.update(existingCompId, compDays || '0', compAvg || '0');
    } else {
      const created = await vacationCompensation.fromDismissal(form.organization_id, id!);
      // Застосувати ручні дні/середню (якщо бухгалтер перебив auto-значення).
      if (created?.id && (compDays || compAvg)) {
        await vacationCompensation.update(
          created.id, compDays || String(created.compensation_days),
          compAvg || String(created.average_daily_wage));
      }
    }
  }
  // Збережений непроведений: зберегти поточні правки і одразу провести (update → post).
  async function onUpdateAndPost() {
    if (!validate()) return;
    setError(null);
    try {
      await update.mutateAsync();
      await ensureCompSaved();  // зафіксувати ручну компенсацію до проведення
      await dismissalOrders.post(id!);
      qc.invalidateQueries({ queryKey: ['dismissal-order', id] });
      qc.invalidateQueries({ queryKey: ['dismissal-orders'] });
      qc.invalidateQueries({ queryKey: ['vacation-compensations'] });
      qc.invalidateQueries({ queryKey: ['employees-full'] });
    } catch (e) {
      setError(extractError(e));
    }
  }

  const empListQ = useQuery({
    queryKey: ['employees-active', form.organization_id],
    queryFn: () => employees.list({
      organization_id: form.organization_id,
      active_only: true,
      limit: 1000,
    }),
    enabled: !!form.organization_id,
  });
  // Поточний працівник документа — для імпортованих наказів на неактивних.
  const empCurQ = useQuery({
    queryKey: ['employee-full', form.employee_id],
    queryFn: () => employees.get(form.employee_id),
    enabled: !!form.employee_id,
  });

  useEffect(() => {
    if (isNew) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : {
        ...f, organization_id: firstOrg,
        employee_id: empFromUrl ?? f.employee_id,
      });
    } else if (docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        employee_id: d.employee_id,
        dismiss_date: d.dismiss_date,
        reason_article: d.reason_article ?? '',
        reason: d.reason ?? '',
        compensation_amount: String(d.compensation_amount),
        comment: d.comment ?? '',
      });
      setDirty(false);
      // Завантажити вже створене нарахування компенсації цього наказа (якщо є) → показати таблицю.
      vacationCompensation.list(d.organization_id, d.id).then((rows) => {
        const c = (rows ?? []).find((r) => !r.deletion_mark);
        if (c) {
          setExistingCompId(c.id);
          setCompDays(String(c.compensation_days ?? ''));
          setCompAvg(String(c.average_daily_wage ?? ''));
          setCompPreview({
            calc_period_start: c.calc_period_start ?? '', calc_period_end: c.calc_period_end ?? '',
            compensation_days: String(c.compensation_days), average_daily_wage: String(c.average_daily_wage),
            compensation_gross_amount: String(c.compensation_gross_amount),
            pdfo_rate: String(c.pdfo_rate ?? ''), wz_rate: String(c.wz_rate ?? ''), esv_rate: String(c.esv_rate ?? ''),
            pdfo_amount: String(c.pdfo_amount), wz_amount: String(c.wz_amount), esv_amount: String(c.esv_amount),
            net_amount: String(c.net_amount),
          });
        }
      }).catch(() => {});
    }
  }, [isNew, docQuery.data, availableOrgs, empFromUrl]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);

  const create = useMutation({
    mutationFn: () => dismissalOrders.create({
      organization_id: form.organization_id,
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      employee_id: form.employee_id,
      dismiss_date: form.dismiss_date,
      reason_article: form.reason_article || null,
      reason: form.reason || null,
      compensation_amount: form.compensation_amount,
      comment: form.comment || null,
    }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['dismissal-orders'] });
      navigate(`/dismissal-orders/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => dismissalOrders.update(id!, {
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      dismiss_date: form.dismiss_date,
      reason_article: form.reason_article || null,
      reason: form.reason || null,
      compensation_amount: form.compensation_amount,
      comment: form.comment || null,
    }),
    onSuccess: () => {
      // Зберегти наказ НЕ перераховує суми нарахування — лише дозволяє «Розрахувати» знову.
      // АЛЕ дата звільнення синхронізується у нарахування на backend одразу → інвалідуємо.
      setDirty(false); setError(null); setCompPreview(null);
      qc.invalidateQueries({ queryKey: ['dismissal-order', id] });
      qc.invalidateQueries({ queryKey: ['vacation-compensations'] });
      qc.invalidateQueries({ queryKey: ['vacation-compensation'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => dismissalOrders.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dismissal-order', id] });
      qc.invalidateQueries({ queryKey: ['dismissal-orders'] });
      qc.invalidateQueries({ queryKey: ['employees-full'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => dismissalOrders.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dismissal-orders'] });
      qc.invalidateQueries({ queryKey: ['vacation-compensations'] });
      qc.invalidateQueries({ queryKey: ['accruals-registry'] });
      navigate('/hr-orders');
    },
    onError: (e) => setError(extractError(e)),
  });

  // UX β: warning якщо новий документ а user не має payroll.manage у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <div style={{
            padding: '1rem', background: '#fef3c7', borderRadius: 6,
            border: '1px solid #fbbf24', color: '#92400e',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати наказ про звільнення у жодній з ваших
            організацій ({totalAccessibleOrgs}). Зверніться до
            адміністратора tenant'а.
          </div>
          <button onClick={() => navigate('/hr-orders')}>Скасувати</button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Новий наказ (звільнення)' : `Наказ (звільнення) № ${form.document_number}`}
          {isPosted && (
            <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>
              ✓ проведено
            </span>
          )}
        </h2>
        <DocumentToolbar
          onBackToList={() => navigate('/hr-orders')}
          onSave={onSave}
          saveDisabled={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={isNew ? onSaveAndPost : onUpdateAndPost}
          postPending={create.isPending || update.isPending}
          postTitle="Зберігає поточні зміни та проводить документ"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={() => {
            confirm('Видалити наказ РАЗОМ зі зв\'язаною компенсацією відпустки?')
              .then((ok) => { if (ok) remove.mutate(); });
          }}
          deletePending={remove.isPending}
          deleteTitle="Видаляє наказ разом зі зв'язаною компенсацією відпустки"
          onPrint={() => navigate(`/dismissal-orders/${id}/print`)}
        />
      </div>

      {v.banner()}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Організація</label>
          <select value={form.organization_id}
                  onChange={(e) => setF({ organization_id: e.target.value })}
                  disabled={!isNew}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field">
          <label>№ наказу</label>
          <input value={form.document_number}
                 onChange={(e) => setF({ document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
                 disabled={isPosted} />
        </div>
        <div className="field">
          <label>Дата наказу</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setF({ document_date: v })}
                 disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Співробітник</label>
          <select value={form.employee_id}
                  onChange={(e) => setF({ employee_id: e.target.value })}
                  disabled={!isNew}>
            <option value="">— оберіть —</option>
            {empCurQ.data
              && !empListQ.data?.items.some((e) => e.id === empCurQ.data!.id) && (
              <option value={empCurQ.data.id}>
                {empCurQ.data.code ? `${empCurQ.data.code} — ` : ''}
                {empCurQ.data.full_name}
              </option>
            )}
            {/* ЦПХ (CIVIL_LAW) НЕ звільняють наказом — це договір, не трудовлаштування.
                Лишаємо лише штатних/без-договору. */}
            {empListQ.data?.items
              .filter((e) => e.employment_type !== 'CIVIL_LAW')
              .map((e) => (
              <option key={e.id} value={e.id}>
                {e.code ? `${e.code} — ` : ''}{e.full_name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Звільнити з дати</label>
          <DatePopover value={form.dismiss_date}
                 onChange={(v) => setF({ dismiss_date: v })}
                 disabled={isPosted} />
        </div>
        {/* Підстава (випадашка) + Коментар — в один рядок. */}
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Підстава (стаття КЗпП)</label>
          <select value={form.reason_article}
                  onChange={(e) => setF({ reason_article: e.target.value })}
                  disabled={isPosted}>
            {/* Значення з БД, якщо його немає у списку (історичні/інші формулювання). */}
            {form.reason_article && !KZOT_ARTICLES.includes(form.reason_article) && (
              <option value={form.reason_article}>{form.reason_article}</option>
            )}
            {KZOT_ARTICLES.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Коментар</label>
          <input value={form.comment}
                 onChange={(e) => setF({ comment: e.target.value })}
                 disabled={isPosted} />
        </div>
      </div>

      {error && <div className="error">{error}</div>}

      {/* Розрахунок компенсації невикористаної відпустки — кнопка активна лише після
          ЗБЕРЕЖЕННЯ/ПРОВЕДЕННЯ наказа (компенсація рахується на основі наказа). */}
      <div style={{ marginTop: '1rem', borderTop: '1px solid #e2e8f0', paddingTop: '0.75rem' }}>
        {/* Проведено → лише таблиця нарахувань (без кнопок Розрахувати/Зберегти),
            як у наказі відпустки. */}
        {!isPosted && (
        <button className="primary"
          disabled={isNew || dirty || !form.employee_id || !form.dismiss_date || compPreviewMut.isPending}
          title={isNew
            ? 'Спершу збережіть або проведіть наказ — тоді можна розрахувати компенсацію'
            : dirty
              ? 'Ви змінили дані наказа. Спершу збережіть зміни («Зберегти») — тоді розрахунок оновиться'
              : 'Розрахувати компенсацію невикористаної відпустки за цим наказом'}
          onClick={() => compPreviewMut.mutate()}>
          🧮 Розрахувати компенсацію відпустки
        </button>
        )}
        {!isNew && !isPosted && dirty && (
          <p style={{ color: '#b45309', fontSize: '0.8rem', marginTop: '0.4rem' }}>
            Ви змінили дані наказа. Натисніть «Зберегти» — після цього можна
            перерахувати компенсацію.
          </p>
        )}
        {isNew && (
          <p style={{ color: '#64748b', fontSize: '0.8rem', marginTop: '0.4rem' }}>
            Кнопка «Розрахувати компенсацію» стане активною після збереження або
            проведення наказа.
          </p>
        )}
        {/* Редаговані Днів + Середня (override) — бухгалтер може перебити (тільки до проведення). */}
        {!isNew && !isPosted && (
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap', marginTop: '0.6rem' }}>
            <div className="field" style={{ width: 150 }}>
              <label>Днів до компенсації</label>
              <input type="number" step="0.01" value={compDays}
                placeholder="залишок"
                onChange={(e) => { setCompDays(e.target.value); setCompPreview(null); }} />
            </div>
            <div className="field" style={{ width: 150 }}>
              <label>Середньоденна</label>
              <input type="number" step="0.01" value={compAvg}
                placeholder="авто"
                onChange={(e) => { setCompAvg(e.target.value); setCompPreview(null); }} />
            </div>
          </div>
        )}
        {compPreview && (
          <div style={{ marginTop: '0.75rem', overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem' }}>
              <thead>
                <tr style={{ background: '#f8fafc', textAlign: 'right' }}>
                  <th style={{ textAlign: 'left', padding: '0.2rem 0.3rem' }}>Розрахунковий період</th>
                  <th style={{ padding: '0.2rem 0.3rem', width: 70 }}>Днів</th>
                  <th style={{ padding: '0.2rem 0.3rem', width: 95 }}>Середня</th>
                  <th style={{ padding: '0.2rem 0.3rem', width: 95 }}>Брутто</th>
                  <th style={{ padding: '0.2rem 0.3rem', width: 95 }}>ПДФО</th>
                  <th style={{ padding: '0.2rem 0.3rem', width: 95 }}>ВЗ</th>
                  <th style={{ padding: '0.2rem 0.3rem', width: 95 }}>ЄСВ</th>
                  <th style={{ padding: '0.2rem 0.3rem', width: 100 }}>До виплати</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderTop: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '0.15rem 0.3rem' }}>{compPreview.calc_period_start} — {compPreview.calc_period_end}</td>
                  <td style={{ padding: '0.15rem 0.3rem', textAlign: 'right' }}>{compPreview.compensation_days}</td>
                  <td style={{ padding: '0.15rem 0.3rem', textAlign: 'right' }}>{compPreview.average_daily_wage}</td>
                  <td style={{ padding: '0.15rem 0.3rem', textAlign: 'right', fontWeight: 700 }}>{compPreview.compensation_gross_amount}</td>
                  <td style={{ padding: '0.15rem 0.3rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <input type="number" className="no-spin pe-cell" readOnly tabIndex={-1}
                        value={trimNum(compPreview.pdfo_amount)}
                        style={{ textAlign: 'right', width: '100%', background: '#f1f5f9', color: '#475569' }} />
                      <InfoQ title={`ПДФО — ${trimNum(compPreview.pdfo_rate)}% від брутто.`} />
                    </div>
                  </td>
                  <td style={{ padding: '0.15rem 0.3rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <input type="number" className="no-spin pe-cell" readOnly tabIndex={-1}
                        value={trimNum(compPreview.wz_amount)}
                        style={{ textAlign: 'right', width: '100%', background: '#f1f5f9', color: '#475569' }} />
                      <InfoQ title={`Військовий збір — ${trimNum(compPreview.wz_rate)}% від брутто.`} />
                    </div>
                  </td>
                  <td style={{ padding: '0.15rem 0.3rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <input type="number" className="no-spin pe-cell" readOnly tabIndex={-1}
                        value={trimNum(compPreview.esv_amount)}
                        style={{ textAlign: 'right', width: '100%', background: '#f1f5f9', color: '#475569' }} />
                      <InfoQ title={esvTipDoc(compPreview.esv_amount, compPreview.esv_rate ?? '22')} />
                    </div>
                  </td>
                  <td style={{ padding: '0.15rem 0.3rem', textAlign: 'right', fontWeight: 700 }}>{compPreview.net_amount}</td>
                </tr>
              </tbody>
            </table>
            {/* Збереження нарахування — лише до проведення (проведено → тільки таблиця). */}
            {!isPosted && (
            <button className="primary" style={{ marginTop: '0.5rem' }}
              disabled={compSaveMut.isPending}
              onClick={() => compSaveMut.mutate()}>
              💾 {existingCompId ? 'Оновити нарахування компенсації' : 'Зберегти нарахування компенсації'}
            </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}
